import React, { useCallback, useContext } from 'react';
import classNames from 'classnames';
import useMatchMedia from 'hooks/useMatchMedia';
import {
  DataRoomMember,
  DataRoomMemberRole,
} from '../../models/DataRoomMember';
import { useTranslation } from 'react-i18next';
import { getUserFullName, hasNotSeenDiscoveryFeature } from 'utils';
import { NewTag } from '../NewTag';
import RoleTag from '../../subrouters/components/RoleTag';
import { Button } from 'ncoded-component-library';
import { isDataRoomOwner } from '../../utils';
import SettingsIcon from 'icons/Settings.icon';
import DataRoomContext from '../../providers/DataRoom/DataRoom.context';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import LeadTag from '../LeadTag';
import MemberRoleInstantChange from './MemberRoleInstantChange';
import ChatIcon from 'icons/Chat.icon';
import { FIRST_RELEASE_DISCOVERING_FEATURE } from 'data/discoveringFeature';
import DiscoveringFeature from 'components/DiscoveringFeature';

import './DataRoomMembersTable.styles.scss';
import './DataRoomMembersTable.styles.responsive.scss';

type DataRoomMembersTableProps = {
  className?: string;
  members: DataRoomMember[];
  isBuySide?: boolean;
  showChat?: boolean;
  showDiscoveryFeature?: boolean;
  onEdit: (member: DataRoomMember) => void;
  onEditMemberRole?: (
    memberId: string,
    updated: Partial<DataRoomMember>,
  ) => void;
};

const DataRoomMembersTable: React.FC<DataRoomMembersTableProps> = (props) => {
  const {
    className,
    showDiscoveryFeature = false,
    isBuySide = false,
    showChat = false,
    members = [],
    onEdit,
    onEditMemberRole,
  } = props;

  const {
    currentUser: { _id, seenDataRoomMessagingFeature },
    updatePreferences,
  } = useContext(CurrentUserContext);
  const { doesntHaveAccess, openChatDrawer } = useContext(DataRoomContext);

  const classes = classNames('data-room-members-table', className);

  const isTablet = useMatchMedia('isTablet');

  const { t } = useTranslation();

  const closeDiscoveringFeature = useCallback(() => {
    if (seenDataRoomMessagingFeature) return;

    updatePreferences({ seenDataRoomMessagingFeature: true });
  }, [seenDataRoomMessagingFeature, updatePreferences]);

  const getCanEditMember = useCallback(
    (role: DataRoomMemberRole, userId: string) => {
      const isOwner = isDataRoomOwner(role);
      const isCurrentUser = userId === _id;

      if (doesntHaveAccess || isOwner || isCurrentUser) return false;

      return true;
    },
    [_id, doesntHaveAccess],
  );

  const renderName = (
    isAccepted: boolean,
    isRequested: boolean,
    email: string,
    fullName: string,
    requestedDataRoomAccessAt: Date,
    isLead: boolean,
  ) => (
    <div className="name">
      <span className={classNames({ invited: !isAccepted || isRequested })}>
        {isAccepted ? fullName : t('invited')}

        {isLead && <LeadTag />}

        {isRequested && (
          <NewTag
            variant="green"
            daysAgo={3}
            time={requestedDataRoomAccessAt}
          />
        )}
      </span>

      {email && <p className="email">{email}</p>}
    </div>
  );

  const renderTeams = useCallback(
    (teams: string[]) => (
      <div className="teams">
        {teams.map((team) => (
          <RoleTag key={team}>{team}</RoleTag>
        ))}
      </div>
    ),
    [],
  );

  const renderRole = useCallback(
    ({ _id, role, qaRole, userId }: DataRoomMember, isQaRole = false) => (
      <MemberRoleInstantChange
        memberId={_id}
        isBuySide={isBuySide}
        {...(isQaRole
          ? {
              qaRole,
            }
          : { role })}
        {...(getCanEditMember(role, userId) && { onChange: onEditMemberRole })}
      />
    ),
    [isBuySide, getCanEditMember, onEditMemberRole],
  );

  const renderEditButton = useCallback(
    (member: DataRoomMember) => {
      if (!getCanEditMember(member.role, member.userId)) return null;

      return (
        <Button
          variant="icon"
          icon={SettingsIcon}
          onClick={() => onEdit(member)}
        />
      );
    },
    [getCanEditMember, onEdit],
  );

  const renderChatButton = useCallback(
    ({ sessionId }: DataRoomMember, index: number) => {
      const showBanner =
        index === 0 &&
        showDiscoveryFeature &&
        hasNotSeenDiscoveryFeature(
          seenDataRoomMessagingFeature,
          FIRST_RELEASE_DISCOVERING_FEATURE,
        );

      return showBanner ? (
        <DiscoveringFeature
          showBannerNotification
          createdAt={FIRST_RELEASE_DISCOVERING_FEATURE}
          title={t('chatWithLeads')}
          description={t('chatWithLeadsDescription')}
          onClose={closeDiscoveringFeature}
          anchor={
            <Button
              className={classNames('chat-button', 'new-feature')}
              variant="icon"
              icon={ChatIcon}
              onClick={() => openChatDrawer(sessionId)}
            />
          }
        />
      ) : (
        <Button
          variant="icon"
          className="chat-button"
          icon={ChatIcon}
          onClick={() => openChatDrawer(sessionId)}
        />
      );
    },
    [
      showDiscoveryFeature,
      seenDataRoomMessagingFeature,
      t,
      closeDiscoveringFeature,
      openChatDrawer,
    ],
  );

  return isTablet ? (
    <div className={classes}>
      {members.map((member, index) => {
        const {
          _id,
          isAccepted,
          requestedDataRoomAccess,
          email,
          firstName,
          lastName,
          teams,
          isLead,
          requestedDataRoomAccessAt,
          sessionId,
        } = member;

        return (
          <div key={_id} className="data-room-members-table__mobile-card">
            <div>
              {renderName(
                isAccepted,
                requestedDataRoomAccess,
                email,
                getUserFullName(firstName, lastName),
                requestedDataRoomAccessAt,
                isLead,
              )}
              <div className="actions">
                {isBuySide &&
                  showChat &&
                  sessionId &&
                  renderChatButton(member, index)}
                {renderEditButton(member)}
              </div>
            </div>
            <div className="tags">
              {renderRole(member)}
              {renderRole(member, true)}
              {renderTeams(teams)}
            </div>
          </div>
        );
      })}
    </div>
  ) : (
    <table className={classes}>
      <thead>
        <tr className="header">
          <th>{t('member')}</th>
          {isBuySide && showChat && <th>{t('chat')}</th>}
          <th>{t('files')}</th>
          <th>{t('q&a')}</th>
          <th>{t('role')}</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {members.map((member, index) => {
          const {
            _id,
            isAccepted,
            requestedDataRoomAccess,
            email,
            firstName,
            lastName,
            teams,
            isLead,
            requestedDataRoomAccessAt,
            sessionId,
          } = member;

          return (
            <tr key={_id}>
              <td>
                {renderName(
                  isAccepted,
                  requestedDataRoomAccess,
                  email,
                  getUserFullName(firstName, lastName),
                  requestedDataRoomAccessAt,
                  isLead,
                )}
              </td>
              {isBuySide && showChat && sessionId && (
                <td>{renderChatButton(member, index)}</td>
              )}
              <td>{renderRole(member)}</td>
              <td>{renderRole(member, true)}</td>
              <td>{renderTeams(teams)}</td>
              <td>
                <div className="actions">{renderEditButton(member)}</div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default DataRoomMembersTable;
